import React, { useCallback, useRef, useState, useEffect } from 'react';
import './AircraftInfoPopup.css';
import { getAircraftSpritePosition, getAircraftSizeMultiplier } from '../utils/aircraftSpriteMap';
import { getAirlineLogo, hasAirlineLogo, getMilitaryLogo, hasMilitaryLogo, useAirlineLogoMapVersion } from '../utils/airlineLogoMap';
import { getUnitsSettings, UNIT_SYSTEMS, convertDistance, convertAltitude } from '../utils/mapStyles';
import { formatEtaTime, formatServerTime } from '../utils/timezones';
import DetectedAircraftType from './DetectedAircraftType';
import spriteImage from '../assets/BelugaProject/beluga_sprites.png';
import { airlineCodeFromCallsign } from '../utils/callsignAirline';
import { isGroundVehicle } from '../utils/groundVehicle';
import { getEmergencySquawk } from '../utils/emergencySquawk';

// Common European/Atlantic airport ICAO → IATA mapping for the route display.
// Falls back to the ICAO when not mapped. Kept inline so the popup has no
// network dependency for the conversion.
const ICAO_TO_IATA_POPUP = {
  EIDW: 'DUB', EICK: 'ORK', EINN: 'SNN', EIKN: 'NOC', EIKY: 'KIR',
  LPPR: 'OPO', LPPT: 'LIS', LPMA: 'FNC', LPFR: 'FAO', LPAZ: 'SMA',
  EGLL: 'LHR', EGKK: 'LGW', EGSS: 'STN', EGLC: 'LCY', EGGW: 'LTN',
  EGCC: 'MAN', EGPF: 'GLA', EGPH: 'EDI', EGAA: 'BFS', EGNX: 'EMA',
  EGNT: 'NCL', EGGD: 'BRS', EGGP: 'LPL', EGBB: 'BHX',
  LFPG: 'CDG', LFPO: 'ORY', LFML: 'MRS', LFLL: 'LYS', LFBO: 'TLS',
  EHAM: 'AMS', EBBR: 'BRU', ELLX: 'LUX',
  EDDF: 'FRA', EDDM: 'MUC', EDDB: 'BER', EDDH: 'HAM', EDDK: 'CGN',
  LSZH: 'ZRH', LOWW: 'VIE',
  LEMD: 'MAD', LEBL: 'BCN', LEMG: 'AGP', LEPA: 'PMI', LEAL: 'ALC',
  LEIB: 'IBZ', LEMH: 'MAH', LEZL: 'SVQ', LEVC: 'VLC',
  LIRF: 'FCO', LIMC: 'MXP', LIPZ: 'VCE', LIRA: 'CIA',
  LGAV: 'ATH', LGTS: 'SKG', LGRP: 'RHO', LGIR: 'HER',
  LKPR: 'PRG', LZIB: 'BTS', LHBP: 'BUD', LROP: 'OTP',
  LTFM: 'IST', LTBA: 'ISL',
  EKCH: 'CPH', ESSA: 'ARN', ENGM: 'OSL', EFHK: 'HEL',
  EPWA: 'WAW', EPKK: 'KRK', EPGD: 'GDN',
  KJFK: 'JFK', KEWR: 'EWR', KORD: 'ORD', KATL: 'ATL', KLAX: 'LAX',
  CYYZ: 'YYZ', CYUL: 'YUL', CYVR: 'YVR',
  OMDB: 'DXB', OTHH: 'DOH', OEJN: 'JED', OERK: 'RUH',
  GMMN: 'CMN', GCLP: 'LPA', GCTS: 'TFS', GCXO: 'TFN',
};
function popupIcaoToIata(icao) {
  if (!icao) return null;
  const k = icao.trim().toUpperCase();
  return ICAO_TO_IATA_POPUP[k] || k;
}

// Unified popup for both selected aircraft and range record dots.
// showPredictionHistory: opt-in — the live WS feed includes prediction_history
// on aircraft that have one, but the table belongs on the Predictions page,
// not the Live map popup.
/**
 * One readout in the metric grid: a small label over a dominant number, with
 * the unit as a light suffix so the figure is what the eye lands on.
 * Renders nothing when there is no value, so the grid closes up rather than
 * leaving a hole.
 */
function Metric({ label, value, unit }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="metric">
      <span className="metric-label">{label}</span>
      <span className="metric-value">
        {value}{unit ? <span className="metric-unit">{unit}</span> : null}
      </span>
    </div>
  );
}

function AircraftInfoPopup({ trackData, rangeRecord, selectedAircraftHex, liveAircraftData, onClose, showPredictionHistory = false }) {
  useAirlineLogoMapVersion();
  // Use ref to store callback to avoid stale closure issues during rapid re-renders
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Track current unit system
  const [unitSystem, setUnitSystem] = useState(() => getUnitsSettings().units);

  // Listen for unit settings changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'displayUnits') {
        setUnitSystem(getUnitsSettings().units);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically for same-tab changes
    const interval = setInterval(() => {
      const currentSettings = getUnitsSettings();
      setUnitSystem(prev => {
        if (prev !== currentSettings.units) {
          return currentSettings.units;
        }
        return prev;
      });
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Get current unit labels
  const units = UNIT_SYSTEMS[unitSystem] || UNIT_SYSTEMS.metric;

  // Handle close with proper touch support
  const handleClose = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onCloseRef.current) {
      onCloseRef.current();
    }
  }, []);

  // Handle touch end separately for reliable touch device support
  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onCloseRef.current) {
      onCloseRef.current();
    }
  }, []);

  // Determine which mode we're in
  // Show range record section if we have range-specific data (distance/bearing)
  const hasRangeData = !!(rangeRecord?.distance ?? rangeRecord?.distanceKm ?? rangeRecord?.bearing);
  const isRangeRecord = hasRangeData;

  // The live aircraft (from the WebSocket map) uses different field names
  // than trackData. Build a normalized fallback so the popup can render
  // immediately on selection — before the HTTP track-points response
  // arrives — and progressively upgrade when richer data lands.
  const liveSource = !isRangeRecord && selectedAircraftHex && liveAircraftData
    ? liveAircraftData[selectedAircraftHex]
    : null;
  const liveFallback = liveSource ? {
    registration: liveSource.r || liveSource.registration,
    callsign: liveSource.flight || liveSource.callsign,
    aircraftType: liveSource.t || liveSource.aircraftType,
    aircraftDescription: liveSource.desc || liveSource.aircraftDescription,
    airlineCode: liveSource.airline_icao,
    airlineName: liveSource.airline_name || liveSource.operator,
    country: liveSource.country,
    countryCode: liveSource.dbFlags_country || liveSource.country_code,
    military: liveSource.military === true,
    groundVehicle: isGroundVehicle(liveSource),
    icao24: liveSource.hex
  } : null;

  const data = trackData || rangeRecord || liveFallback;

  if (!data) {
    return null;
  }

  // Extract common fields
  const {
    registration,
    callsign,
    aircraftType,
    aircraftDescription,
    airlineCode,
    airlineName,
    country,
    countryCode,
    military,
    photo
  } = data;

  // Range record specific fields (HTTP API uses distanceKm, WebSocket uses distance)
  const distance = rangeRecord?.distance ?? rangeRecord?.distanceKm;
  const bearing = rangeRecord?.bearing;
  const compassDirection = rangeRecord?.compassDirection;
  const rangeAltitude = rangeRecord?.altitude;
  const rangeTimestamp = rangeRecord?.rangeTimestamp ?? rangeRecord?.timestamp;
  const isNew = rangeRecord?.isNew;

  // Track data specific fields
  const trackLengthKm = trackData?.trackLengthKm;
  const furthestDistanceKm = trackData?.furthestDistanceKm;
  const totalPoints = trackData?.totalPoints;

  // ICAO identifier (different field names across sources)
  const icaoId = trackData?.icao24 || rangeRecord?.icao24 || rangeRecord?.icaoAddress;

  // Hex to write a photo-derived type against. Same as icaoId, plus the live
  // feed's hex for aircraft selected on the map before the track response lands.
  const saveIcao24 = icaoId || liveSource?.hex;

  // Format timestamp for display
  const formatTime = (ts) => {
    try {
      const date = new Date(ts);
      return date.toLocaleString(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
        hour12: false
      });
    } catch (e) {
      return ts;
    }
  };

  // Get current live aircraft data (only for track mode) — same source we
  // already resolved above for the metadata fallback.
  const liveAircraft = liveSource;
  const currentLat = liveAircraft?.lat;
  const currentLon = liveAircraft?.lon;
  const currentAltitude = liveAircraft?.alt_baro;
  const currentSpeed = liveAircraft?.gs;
  const currentHeading = liveAircraft?.true_heading || liveAircraft?.mag_heading || liveAircraft?.track;

  // Calculate dynamic track length and points including live positions
  const calculateTrackStats = () => {
    if (!trackData?.points || trackData.points.length === 0) {
      return { length: trackLengthKm || 0, points: totalPoints || 0 };
    }

    let points = [...trackData.points];

    // Add live position if available and newer than last track point
    if (liveAircraft && currentLat && currentLon) {
      const lastTrackPoint = points[points.length - 1];
      const lastTrackTime = new Date(lastTrackPoint.timestamp).getTime();
      const liveTime = liveAircraft.lastSeen || Date.now();

      if (liveTime - lastTrackTime >= 5000) {
        points.push({
          latitude: currentLat,
          longitude: currentLon,
          timestamp: new Date(liveTime).toISOString()
        });
      }
    }

    // Calculate total distance using Haversine formula
    let totalDistance = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];

      const R = 6371;
      const dLat = (p2.latitude - p1.latitude) * Math.PI / 180;
      const dLon = (p2.longitude - p1.longitude) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(p1.latitude * Math.PI / 180) * Math.cos(p2.latitude * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      totalDistance += R * c;
    }

    return { length: totalDistance, points: points.length };
  };

  const { length: dynamicTrackLength, points: dynamicTrackPoints } = !isRangeRecord
    ? calculateTrackStats()
    : { length: 0, points: 0 };

  // Get logo - use military insignia for military aircraft, airline logo otherwise
  let hasLogo = false;
  let logoPath = null;

  // Try to derive airline code from callsign if not provided — never for
  // military aircraft, never when the callsign is the registration
  // A ground vehicle has no operator at all — its callsign is a fleet ID.
  const groundVehicle = data.groundVehicle === true || isGroundVehicle(data);
  let derivedAirlineCode = groundVehicle ? null : airlineCode;
  if (!groundVehicle && !derivedAirlineCode && callsign) {
    const code = airlineCodeFromCallsign(callsign, { registration, military });
    if (code && hasAirlineLogo(code)) {
      derivedAirlineCode = code;
    }
  }
  const operatorName = groundVehicle ? null : airlineName;

  if (military) {
    if (countryCode && hasMilitaryLogo(countryCode)) {
      hasLogo = true;
      logoPath = getMilitaryLogo(countryCode, airlineName);
    }
  } else {
    if (derivedAirlineCode && hasAirlineLogo(derivedAirlineCode)) {
      hasLogo = true;
      logoPath = getAirlineLogo(derivedAirlineCode, 'icon');
    }
  }

  // Get aircraft sprite position for icon
  const spritePos = getAircraftSpritePosition(aircraftType);
  const sizeMultiplier = getAircraftSizeMultiplier(aircraftType);
  const iconSize = Math.max(32, Math.round(48 * sizeMultiplier)); // Minimum 32px
  const bgPosX = -(spritePos.col * iconSize);
  const bgPosY = -(spritePos.row * iconSize);

  // Aircraft icon style with white color to match table header - only show if we have aircraft type
  const showAircraftIcon = !!aircraftType;
  const aircraftIconStyle = showAircraftIcon ? {
    width: `${iconSize}px`,
    height: `${iconSize}px`,
    backgroundSize: `${iconSize * 8}px auto`,
    backgroundPosition: `${bgPosX}px ${bgPosY}px`,
    backgroundImage: `url(${spriteImage})`,
    backgroundRepeat: 'no-repeat',
    display: 'inline-block',
    transform: 'rotate(90deg)', // Point right like on map
    filter: 'brightness(0) invert(1)' // White icon
  } : null;

  // Squawk 7500/7600/7700. Read from the LIVE aircraft, not the track record: the
  // squawk is a right-now condition and a historical track must not keep claiming an
  // emergency the aircraft has since cancelled.
  const emergency = getEmergencySquawk(liveAircraft);

  // Add class for range record mode (smaller on mobile)
  const popupClass = `aircraft-info-popup${isRangeRecord ? ' range-record-popup' : ''}${emergency ? ' emergency-popup' : ''}`;

  return (
    <div className={popupClass}>
      <div className="popup-header">
        {showAircraftIcon && aircraftIconStyle && (
          <div className="aircraft-icon" style={aircraftIconStyle}></div>
        )}
        <div className="header-info">
          <div className="callsign-registration">
            <span className="callsign">{callsign || registration || '-'}</span>
            {liveAircraft?.flight_iata && (
              <span className="flight-iata"> {liveAircraft.flight_iata}</span>
            )}
            {callsign && registration && (
              <span className="registration-small">({registration})</span>
            )}
          </div>
          {icaoId && (
            <div className="icao-hex">ICAO: {icaoId.toUpperCase()}</div>
          )}
        </div>
        {onClose && (
          <button
            className="popup-close-btn"
            onClick={handleClose}
            onTouchEnd={handleTouchEnd}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        )}
      </div>

      {emergency && (
        <div className="popup-emergency-banner" role="alert">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            <path d="M12 9v4" /><path d="M12 17h.01" />
          </svg>
          <span className="popup-emergency-code">{emergency.code}</span>
          <span className="popup-emergency-label">{emergency.label}</span>
          <span className="popup-emergency-desc">{emergency.description}</span>
        </div>
      )}

      <div className="popup-content">
        {(operatorName || derivedAirlineCode) && (
          <div className="info-row airline-name-row">
            {hasLogo && logoPath && (
              <img
                src={logoPath}
                alt={operatorName || derivedAirlineCode}
                className="airline-logo-inline"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <strong>{operatorName || derivedAirlineCode}</strong>
          </div>
        )}

        {/* Identity meta: type and country on ONE line. A flag beside a country
            name needs no "Country:" label, and the type designator is a chip
            rather than a repeat of the words next to it. */}
        {(aircraftDescription || aircraftType || country) && (
          <div className="identity-meta">
            {(aircraftDescription || aircraftType) && (
              <span className="identity-type">
                {aircraftDescription
                  ? aircraftDescription.replace(/,\s*/g, ' ')
                  : aircraftType}
                {aircraftDescription && aircraftType && (
                  <span className="aircraft-type-code">{aircraftType}</span>
                )}
              </span>
            )}
            {country && (
              <span className="identity-country">
                {countryCode && (
                  <img
                    src={`https://flagcdn.com/16x12/${countryCode.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/32x24/${countryCode.toLowerCase()}.png 2x,
                            https://flagcdn.com/48x36/${countryCode.toLowerCase()}.png 3x`}
                    width="16"
                    height="12"
                    alt={country}
                    className="country-flag"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                {country}
              </span>
            )}
          </div>
        )}

        {/* Route — origin → destination IATA codes when known. The
            backend includes origin_iata/origin_name on the live aircraft
            payload when a flight_schedule entry matches the callsign;
            we always show the destination converted to IATA from
            dest_airport (ICAO). Useful for verifying our inbound
            classification matches the aircraft's actual scheduled route
            (an over-flight to MAN being labelled "inbound DUB" stands
            out at a glance). */}
        {liveAircraft?.dest_airport && (
          <div className="info-row">
            <span className="label">Route</span>
            <span className="value" style={{fontWeight: 'bold'}} title={
              [liveAircraft.origin_name, liveAircraft.dest_airport]
                .filter(Boolean).join(' → ')
            }>
              {liveAircraft.origin_iata || (liveAircraft.origin_icao ? popupIcaoToIata(liveAircraft.origin_icao) : '?')}
              {' → '}
              {popupIcaoToIata(liveAircraft.dest_airport)}
            </span>
          </div>
        )}

        {/* Prediction History (opt-in via showPredictionHistory) */}
        {showPredictionHistory && liveAircraft?.prediction_history && liveAircraft.prediction_history.length > 0 && (
          <>
            <div className="section-header">Prediction History</div>
            <div className="prediction-history-table" style={{fontSize: '0.8em', maxHeight: '150px', overflowY: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr style={{borderBottom: '1px solid var(--border-color, #444)'}}>
                    <th style={{textAlign: 'left', padding: '2px 4px'}}>Time</th>
                    <th style={{textAlign: 'right', padding: '2px 4px'}}>ETA</th>
                    <th style={{textAlign: 'right', padding: '2px 4px'}}>Dist</th>
                    <th style={{textAlign: 'right', padding: '2px 4px'}}>Alt</th>
                  </tr>
                </thead>
                <tbody>
                  {[...liveAircraft.prediction_history].reverse().map((entry, i) => (
                    <tr key={i} style={{borderBottom: '1px solid var(--border-color-faint, #333)'}}>
                      <td style={{padding: '1px 4px'}}>{formatServerTime(entry.t, liveAircraft.eta_airport)}</td>
                      <td style={{textAlign: 'right', padding: '1px 4px', fontWeight: 'bold',
                        color: entry.eta > 15 ? 'var(--color-success, #4caf50)' :
                               entry.eta > 5 ? 'var(--color-warning, #ff9800)' :
                               'var(--color-danger, #f44336)'
                      }}>{formatEtaTime(entry.eta_time, liveAircraft.eta_airport)}</td>
                      <td style={{textAlign: 'right', padding: '1px 4px'}}>{convertDistance(entry.dist * 1.852, unitSystem)?.toFixed(1)}{units.distance}</td>
                      <td style={{textAlign: 'right', padding: '1px 4px'}}>{Math.round(convertAltitude(entry.alt, unitSystem))?.toLocaleString()}{units.altitude}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Range Record specific section */}
        {isRangeRecord && (
          <>
            <div className="metrics">
              <div className="metric-row">
                <Metric label="Distance"
                        value={distance != null ? convertDistance(distance, unitSystem).toFixed(1) : null}
                        unit={units.distance} />
                <Metric label="Direction"
                        value={bearing != null
                          ? `${compassDirection || ''} ${Math.round(bearing)}`.trim() : null}
                        unit="°" />
                <Metric label="Altitude"
                        value={rangeAltitude
                          ? Math.round(convertAltitude(rangeAltitude, unitSystem)).toLocaleString() : null}
                        unit={units.altitude} />
              </div>
              {rangeTimestamp && (
                <div className="metric-row">
                  <Metric label="Recorded" value={formatTime(rangeTimestamp)} />
                </div>
              )}
            </div>

            {isNew && (
              <div className="range-new-badge">NEW RECORD</div>
            )}
          </>
        )}

        {/* Live telemetry and track summary as one dense readout.
            Six numbers do not need six rows and two headings: three to a row,
            grouped by a single gap, with the coordinate pair demoted to a
            footer because it is reference data rather than something read at a
            glance. */}
        {!isRangeRecord && (liveAircraft || dynamicTrackLength > 0 || dynamicTrackPoints > 0) && (
          <div className="metrics">
            {liveAircraft && (
              <div className="metric-row">
                <Metric label="Altitude"
                        value={currentAltitude !== undefined
                          ? Math.round(convertAltitude(currentAltitude, unitSystem)).toLocaleString() : null}
                        unit={units.altitude} />
                <Metric label="Speed"
                        value={currentSpeed !== undefined
                          ? (unitSystem === 'aeronautical'
                              ? Math.round(currentSpeed)
                              : unitSystem === 'imperial'
                                ? Math.round(currentSpeed * 1.15078)
                                : Math.round(currentSpeed * 1.852))
                          : null}
                        unit={unitSystem === 'aeronautical' ? 'kts' : unitSystem === 'imperial' ? 'mph' : 'km/h'} />
                {/* Three digits is the aviation convention and keeps the
                    column from resizing as the heading changes. */}
                <Metric label="Heading"
                        value={currentHeading !== undefined
                          ? String(Math.round(currentHeading)).padStart(3, '0') : null}
                        unit="°" />
              </div>
            )}

            {(dynamicTrackLength > 0 || furthestDistanceKm !== undefined || dynamicTrackPoints > 0) && (
              <div className="metric-row">
                <Metric label="Track"
                        value={dynamicTrackLength > 0
                          ? convertDistance(dynamicTrackLength, unitSystem).toFixed(1) : null}
                        unit={units.distance} />
                <Metric label="Max dist"
                        value={furthestDistanceKm !== undefined
                          ? convertDistance(furthestDistanceKm, unitSystem).toFixed(1) : null}
                        unit={units.distance} />
                <Metric label="Points"
                        value={dynamicTrackPoints > 0 ? dynamicTrackPoints : null} />
              </div>
            )}

            {/* Footer line: where it is on the left, when it arrives on the
                right. Both are reference figures rather than live telemetry,
                so they share the quiet line under the readout. */}
            {!isRangeRecord && (
              (currentLat !== undefined && currentLon !== undefined) || liveAircraft?.eta_time
            ) && (
              <div className="coords-footer">
                <span className="coords-pos">
                  {currentLat !== undefined && currentLon !== undefined
                    ? `${currentLat.toFixed(4)}°, ${currentLon.toFixed(4)}°`
                    : ''}
                </span>
                {liveAircraft?.eta_time && (
                  <span className="coords-eta">
                    <span className="coords-eta-label">ETA</span>
                    <span
                      className="coords-eta-value"
                      style={{
                        color: (liveAircraft.eta_minutes || 0) > 15 ? 'var(--color-success, #4caf50)' :
                               (liveAircraft.eta_minutes || 0) > 5 ? 'var(--color-warning, #ff9800)' :
                               'var(--color-danger, #f44336)'
                      }}
                    >
                      {formatEtaTime(liveAircraft.eta_time, liveAircraft.eta_airport)}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Photo section (both modes) */}
        {photo && photo.largeThumbnailUrl && (
          <div className="photo-section">
            <a
              href={photo.imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="photo-link"
            >
              <img
                src={photo.largeThumbnailUrl}
                alt={`${registration || callsign}`}
                className="aircraft-photo"
                loading="lazy"
              />
            </a>
            {photo.photographer && (
              <div className="photo-credit">
                Photo by {photo.photographer}
              </div>
            )}
          </div>
        )}

        {/* The photo URL names the aircraft type. When we have no type for this
            hex, or the photo contradicts it (recycled registration), offer it —
            admins can write it to the aircraft row in one click. */}
        {photo && (
          <DetectedAircraftType
            icao24={saveIcao24}
            photoUrl={photo.imageUrl}
            knownType={aircraftType}
          />
        )}
      </div>
    </div>
  );
}

export default AircraftInfoPopup;
